import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Snackbar, Stack, Typography } from "@mui/material";
import { useAuthContext } from "../context/AuthContext";
import { useSocketContext } from "../context/SocketContext";
import { openAgentVideoJoinUrl } from "../utils/activeVideoUi";
import {
  publishVirtualOfficePauseTracks,
  readVirtualOfficeSession,
} from "../utils/virtualOfficeSessionBridge";
import { patchVirtualOfficePresence } from "../api/client";

function parsePayload(raw) {
  let payload = raw;
  if (typeof raw === "string") {
    try {
      payload = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!payload || typeof payload !== "object") return null;
  return payload;
}

function isVoPrivateInvite(payload) {
  if (!payload || String(payload.action || "") !== "started") return false;
  if (!payload.isPrivate || !payload.agentJoinUrl) return false;
  const ctx = String(payload.callContext || "").toLowerCase();
  if (ctx === "vo_private" || ctx === "vo_huddle") return true;
  try {
    const u = new URL(String(payload.agentJoinUrl), window.location.origin);
    const c = u.searchParams.get("context");
    return c === "vo_private" || c === "vo_huddle";
  } catch {
    return !payload.teamSpaceId && !payload.agendaEventId && !payload.leadId;
  }
}

/**
 * Banner global: convite de conversa privada do Escritório Virtual,
 * visível em qualquer tela do MainLayout (não só no cockpit VO).
 */
export default function VirtualOfficeInviteBanner() {
  const { userData } = useAuthContext();
  const { socket } = useSocketContext() || {};
  const myUserId = Number(userData?.id) || 0;
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    if (!socket) return undefined;
    const onVideoRoom = (raw) => {
      const payload = parsePayload(raw);
      if (!isVoPrivateInvite(payload)) return;
      const toUid = Number(payload.toUserId);
      if (Number.isFinite(toUid) && toUid > 0 && toUid !== myUserId) return;
      setInvite({
        joinUrl: payload.agentJoinUrl,
        roomId: Number(payload.roomId) || null,
        title: "Convite para conversa privada no escritório",
      });
    };
    socket.on("video_room", onVideoRoom);
    return () => socket.off("video_room", onVideoRoom);
  }, [socket, myUserId]);

  const dismiss = useCallback(async () => {
    setInvite(null);
    const session = readVirtualOfficeSession();
    if (session?.roomId) {
      try {
        await patchVirtualOfficePresence({ status: "present" });
      } catch {
        /* ignore */
      }
    }
  }, []);

  const accept = useCallback(() => {
    if (!invite?.joinUrl) return;
    publishVirtualOfficePauseTracks({ title: "Conversa privada" });
    openAgentVideoJoinUrl(invite.joinUrl, {
      context: "vo_private",
      name: userData?.nome || userData?.name || "",
    });
    setInvite(null);
  }, [invite, userData]);

  if (!invite) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{ top: { xs: 72, sm: 80 } }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: "center", minWidth: { xs: "90vw", sm: 420 } }}
        action={
          <Stack direction="row" spacing={0.5}>
            <Button color="inherit" size="small" onClick={accept}>
              Entrar
            </Button>
            <Button color="inherit" size="small" onClick={() => void dismiss()}>
              Agora não
            </Button>
          </Stack>
        }
      >
        <Typography variant="body2" fontWeight={600}>
          {invite.title}
        </Typography>
        <Typography variant="caption" display="block">
          O piso do escritório permanece aberto. Você pode entrar ou recusar.
        </Typography>
      </Alert>
    </Snackbar>
  );
}
